import React from 'react';
import { Modal } from '../Shared/Modal';
import { LogLevelBadge } from '../../logs/LogLevelBadge';
import type { MigrationLogEntry } from '../../../types/logs';

const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

interface LogDetailModalProps {
  log: MigrationLogEntry;
  isOpen: boolean;
  onClose: () => void;
}

export const LogDetailModal: React.FC<LogDetailModalProps> = ({ log, isOpen, onClose }) => {
  const borderColor =
    log.level === 'Error' ? 'border-red-200 dark:border-red-800' :
      log.level === 'Warning' ? 'border-orange-200 dark:border-orange-700' :
        log.level === 'Success' ? 'border-emerald-200 dark:border-emerald-800' :
          'border-blue-200 dark:border-blue-800';

  const headerBg =
    log.level === 'Error' ? 'bg-red-50 dark:bg-red-900/20' :
      log.level === 'Warning' ? 'bg-orange-50 dark:bg-orange-900/20' :
        log.level === 'Success' ? 'bg-emerald-50 dark:bg-emerald-900/20' :
          'bg-blue-50 dark:bg-blue-900/20';

  const headerBorder =
    log.level === 'Error' ? 'border-red-200 dark:border-red-800' :
      log.level === 'Warning' ? 'border-orange-200 dark:border-orange-700' :
        log.level === 'Success' ? 'border-emerald-200 dark:border-emerald-800' :
          'border-blue-200 dark:border-blue-800';

  const labelText =
    log.level === 'Error' ? 'text-red-600 dark:text-red-400' :
      log.level === 'Warning' ? 'text-orange-700 dark:text-orange-500' :
        log.level === 'Success' ? 'text-emerald-700 dark:text-emerald-400' :
          'text-blue-700 dark:text-blue-400';

  const keyText =
    log.level === 'Error' ? 'text-red-700 dark:text-red-400' :
      log.level === 'Warning' ? 'text-orange-700 dark:text-orange-400' :
        log.level === 'Success' ? 'text-emerald-700 dark:text-emerald-400' :
          'text-blue-700 dark:text-blue-400';

  const iconColor =
    log.level === 'Error' ? 'text-red-400 dark:text-red-500' :
      log.level === 'Warning' ? 'text-orange-500 dark:text-orange-400' :
        log.level === 'Success' ? 'text-emerald-500 dark:text-emerald-400' :
          'text-blue-500 dark:text-blue-400';

  const dividerColor =
    log.level === 'Error' ? 'divide-red-100 dark:divide-red-900/30' :
      log.level === 'Warning' ? 'divide-orange-100 dark:divide-orange-900/30' :
        log.level === 'Success' ? 'divide-emerald-100 dark:divide-emerald-900/30' :
          'divide-blue-100 dark:divide-blue-900/30';

  const docLinkText =
    log.level === 'Error' ? 'text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300' :
      log.level === 'Warning' ? 'text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300' :
        log.level === 'Success' ? 'text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300' :
          'text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Log Entry Details" variant="view" showCloseButton>
      <div className="space-y-4">

        {/* Meta row */}
        <div className="flex flex-wrap items-center gap-2">
          <LogLevelBadge level={log.level} />
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
            {formatTimestamp(log.timestamp)}
          </span>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
            {log.stage}
          </span>
          {log.resourceType && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700">
              {log.resourceType}{log.resourceName && `::${log.resourceName}`}
            </span>
          )}
        </div>

        {/* Message */}
        <div className={`rounded-xl overflow-hidden border ${borderColor}`}>
          <div className={`flex items-center gap-2 px-4 py-2.5 border-b ${headerBg} ${headerBorder}`}>
            <span className={`text-xs font-bold uppercase tracking-wider ${labelText}`}>
              {log.level === 'Error' ? 'Error Log' : 'Message'}
            </span>
          </div>
          <div className="px-4 py-3.5 bg-white dark:bg-gray-800 max-h-40 overflow-y-auto">
            <p className="text-sm break-words leading-relaxed text-gray-700 dark:text-gray-300">
              {log.message}
            </p>
          </div>
        </div>

        {/* Additional Details */}
        {log.details && Object.keys(log.details).length > 0 && (
          <div className={`rounded-xl overflow-hidden border ${borderColor}`}>
            <div className={`px-4 py-2.5 border-b ${headerBg} ${headerBorder}`}>
              <span className={`text-xs font-bold uppercase tracking-wider ${labelText}`}>
                Additional Details
              </span>
            </div>
            <dl className={`divide-y ${dividerColor}`}>
              {Object.entries(log.details).map(([key, value]) => (
                <div key={key} className="grid grid-cols-3 px-4 py-2.5 bg-white dark:bg-gray-800">
                  <dt className={`text-xs font-semibold col-span-1 truncate pr-2 ${keyText}`}>
                    {key}
                  </dt>
                  <dd className="text-xs text-gray-700 dark:text-gray-300 break-words font-mono col-span-2 min-w-0 max-h-64 overflow-y-auto">
                    {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {/* Recommendation */}
        <div className={`rounded-xl overflow-hidden border ${borderColor}`}>
          <div className={`px-4 py-2.5 border-b ${headerBg} ${headerBorder}`}>
            <span className={`text-xs font-bold uppercase tracking-wider ${labelText}`}>
              Recommendation
            </span>
          </div>
          <div className="px-4 py-3.5 bg-white dark:bg-gray-800 flex items-start gap-3">
            <svg
              className={`w-4 h-4 shrink-0 mt-0.5 ${log.recommendedAction ? iconColor : 'text-gray-300 dark:text-gray-600'}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {log.recommendedAction
              ? <p className="text-sm text-gray-700 dark:text-gray-300 italic">{log.recommendedAction}</p>
              : <p className="text-sm text-gray-400 dark:text-gray-500 italic">No recommendation available for this log entry.</p>
            }
          </div>
        </div>

        {/* Documentation */}
        <div className={`pt-3 border-t flex items-center gap-2 ${headerBorder}`}>
          <svg
            className={`w-4 h-4 shrink-0 ${log.documentationUrl ? iconColor : 'text-gray-300 dark:text-gray-600'}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          {log.documentationUrl
            ? <a
              href={log.documentationUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-1.5 text-sm font-medium ${docLinkText}`}
            >
              View Documentation
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
            : <span className="text-sm text-gray-400 dark:text-gray-500 italic">No documentation available for this log entry.</span>
          }
        </div>

      </div>
    </Modal>
  );
};
